import { useEffect, useState } from 'react';

function getResponsiveMediaState() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return {
            canAutoplayDecorativeVideo: false,
            canHoverPreviewVideo: false,
        };
    }

    const canHoverPreviewVideo = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    return {
        canAutoplayDecorativeVideo: !prefersReducedMotion,
        canHoverPreviewVideo,
    };
}

export function useResponsiveMedia() {
    const [state, setState] = useState(getResponsiveMediaState);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const queries = [
            window.matchMedia('(hover: hover) and (pointer: fine)'),
            window.matchMedia('(prefers-reduced-motion: reduce)'),
        ];

        const update = () => setState(getResponsiveMediaState());

        queries.forEach((query) => {
            if (typeof query.addEventListener === 'function') {
                query.addEventListener('change', update);
                return;
            }

            if (typeof query.addListener === 'function') {
                query.addListener(update);
            }
        });

        update();

        return () => {
            queries.forEach((query) => {
                if (typeof query.removeEventListener === 'function') {
                    query.removeEventListener('change', update);
                    return;
                }

                if (typeof query.removeListener === 'function') {
                    query.removeListener(update);
                }
            });
        };
    }, []);

    return state;
}

export function syncAutoplayVideo(video) {
    if (!video) {
        return () => {};
    }

    let stopped = false;
    let retryTimerId = null;
    let animationFrameId = null;
    let attemptCount = 0;
    const maxAttempts = 24;
    const retryDelay = 250;

    const attemptPlay = () => {
        if (stopped || !video.isConnected) {
            return;
        }

        video.autoplay = true;
        video.defaultMuted = true;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('autoplay', '');
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        if (video.readyState === 0) {
            video.load?.();
        }

        const playPromise = video.play?.();
        playPromise?.catch?.(() => {});
    };

    const handleReady = () => attemptPlay();
    const handlePageResume = () => {
        animationFrameId = window.requestAnimationFrame(() => {
            animationFrameId = window.requestAnimationFrame(() => {
                attemptPlay();
            });
        });
    };
    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            handlePageResume();
        }
    };
    const handlePause = () => {
        if (!stopped && document.visibilityState === 'visible') {
            handlePageResume();
        }
    };
    const scheduleRetry = () => {
        if (stopped || attemptCount >= maxAttempts) {
            return;
        }

        retryTimerId = window.setTimeout(() => {
            attemptCount += 1;
            attemptPlay();
            scheduleRetry();
        }, retryDelay);
    };

    video.addEventListener('loadedmetadata', handleReady);
    video.addEventListener('loadeddata', handleReady);
    video.addEventListener('canplay', handleReady);
    video.addEventListener('canplaythrough', handleReady);
    video.addEventListener('pause', handlePause);
    window.addEventListener('load', handlePageResume);
    window.addEventListener('pageshow', handlePageResume);
    window.addEventListener('focus', handlePageResume);
    window.addEventListener('orientationchange', handlePageResume);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    attemptPlay();
    handlePageResume();
    scheduleRetry();

    return () => {
        stopped = true;
        if (retryTimerId) {
            window.clearTimeout(retryTimerId);
        }
        if (animationFrameId) {
            window.cancelAnimationFrame(animationFrameId);
        }
        video.removeEventListener('loadedmetadata', handleReady);
        video.removeEventListener('loadeddata', handleReady);
        video.removeEventListener('canplay', handleReady);
        video.removeEventListener('canplaythrough', handleReady);
        video.removeEventListener('pause', handlePause);
        window.removeEventListener('load', handlePageResume);
        window.removeEventListener('pageshow', handlePageResume);
        window.removeEventListener('focus', handlePageResume);
        window.removeEventListener('orientationchange', handlePageResume);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
}
